import { StatusProcessContactGroup } from 'consts/data';
import { ITEM_PER_PAGE } from 'consts/filters';
import { calculateRemainingTime } from 'helpers/calculate';
import { cloneDeep, get, isArray, isEmpty, isObject, isPlainObject, set } from 'lodash';

class ContactGroupModel {
  constructor({
    blocked = 0,
    clicked = 0,
    code = 0,
    contacts = [],
    country = '',
    createDate = '',
    scrubDate = '',
    delivered = '',
    failed = 0,
    id = '',
    isEnriching = false,
    isImporting = false,
    isScrubbing = false,
    isRefreshing = false,
    leadCallbackFail = 0,
    leadDuplicated = 0,
    leadFiltered = 0,
    leadOptout = 0,
    leadSystemBlacklisted = 0,
    leadTotal = 0,
    metadata = { carrier: [], imported: 0, dnc: 0, landline: 0, leadFilter: [] },
    name = '',
    optout = 0,
    triggered = 0,
    sent = 0,
    userId = '',
    type = 0,
  } = {}) {
    this.blocked = blocked;
    this.clicked = clicked;
    this.code = code;
    this.contacts = contacts;
    this.country = country;
    this.createDate = createDate;
    this.scrubDate = scrubDate;
    this.delivered = delivered;
    this.failed = failed;
    this.id = id;
    this.isEnriching = isEnriching;
    this.isImporting = isImporting;
    this.isScrubbing = isScrubbing;
    this.isRefreshing = isRefreshing;
    this.leadCallbackFail = leadCallbackFail;
    this.leadDuplicated = leadDuplicated;
    this.leadFiltered = leadFiltered;
    this.leadOptout = leadOptout;
    this.leadSystemBlacklisted = leadSystemBlacklisted;
    this.leadTotal = leadTotal;
    this.metadata = metadata;
    this.name = name;
    this.optout = optout;
    this.triggered = triggered;
    this.sent = sent;
    this.userId = userId;
    this.type = type;
  }

  static initialFilters(filters = {}) {
    const { name, size, page, country } = filters || {};
    return {
      name: name || '',
      size: size || ITEM_PER_PAGE,
      page: page || 0,
      country: country || '',
    };
  }

  //! Request
  static parseRequestUpdateLeadFilter(values) {
    return {
      leadFilter: values?.leadFilter || [],
      filteredGroupId: values?.filteredGroupId?.value || null,
    };
  }

  static parseRequestToGetDetail(values) {
    return {
      groupId: values?.groupId || '',
      pageSize: values?.pageSize || 50,
      page: values?.page || 1,
      searchValue: values?.searchValue || '',
      sortField: values?.sortField || 'creation_date',
      direction: values?.direction || 'desc',
    };
  }

  static parseRequestFilterSuppression(values) {
    return {
      newGroupName: values?.newGroupName || '',
      dataGroupIds: values?.dataGroupIds?.map((el) => el.id) || [],
      suppressionGroupIds: values?.suppressionGroupIds?.map((el) => el.id) || [],
    };
  }

  static parseRequestSearchContact(data) {
    const {
      pageIndex,
      pageSize,
      targets,
      sortDirections: direction,
      sortFields: sortField,
      phoneType,
    } = data;

    const page = pageIndex + 1;
    const filters = {
      page,
      pageSize,
      targets,
      direction,
      sortField,
      phoneType,
    };

    return filters;
  }

  //! Response
  static parseContactDetailFromReponse(response) {
    if (isObject(response?.data)) {
      const nextData = new ContactGroupModel(response?.data);
      return nextData;
    }

    return {};
  }

  static modeExportContacts = {
    isAll: 'all',
    isByConditions: 'isByConditions',
  };

  static importHandler = {
    parseHeadersByAI: (headersFromAI, { onlyGetManual = false } = {}) => {
      if (!isArray(headersFromAI)) {
        return {};
      }

      const tempObj = {};
      for (let i = 0; i < headersFromAI.length; i++) {
        const el = headersFromAI[i];
        const { fileName, header } = el;
        if (onlyGetManual) {
          if (!el.mappedByAi && header) {
            tempObj[fileName] = header ? JSON.parse(header) : {};
          }
          continue;
        }

        if (el.mappedByAi && header) {
          tempObj[fileName] = header ? JSON.parse(header) : {};
        }
      }

      return tempObj;
    },

    initHeaderEachPreviewFile: (previewLine) => {
      if (!isArray(previewLine)) {
        return [];
      }

      const tmpHeader = [];
      (previewLine?.[0] || []).forEach((item, index) => {
        tmpHeader.push(undefined);
        // tmpHeader.push({
        //   label: '',
        //   value: '',
        // });
      });

      return tmpHeader;
    },

    parseColumnEachPreviewFile: (previewLine) => {
      if (!isArray(previewLine)) {
        return [];
      }

      const tmpColumns = [];
      (previewLine?.[0] || []).forEach((item, index) => {
        tmpColumns.push({
          Header: index.toString(),
          accessor: index.toString(),
        });
      });

      return tmpColumns;
    },

    checkAllFileHasPhoneField: (headers) => {
      if (isEmpty(headers)) {
        return false;
      }

      let isChecked = true;

      const objToArr = Object.entries(headers);
      for (let i = 0; i < objToArr.length; i++) {
        const headerSelected = objToArr[i][1];
        if (!headerSelected.some((el) => el?.value === 'phone')) {
          isChecked = false;
        }
      }

      return isChecked;
    },

    parseFileHeadersToImport: (fileHeaders) => {
      if (isEmpty(fileHeaders)) {
        return {};
      }

      const tempFileHeaders = cloneDeep(fileHeaders);

      Object.keys(tempFileHeaders).forEach((nameFile) => {
        if (isArray(tempFileHeaders?.[nameFile])) {
          tempFileHeaders[nameFile] = JSON.stringify(
            tempFileHeaders[nameFile]
              .map((el, index) => {
                return {
                  index,
                  value: el?.value,
                };
              })
              .filter((el) => !!el.value)
              .reduce((nextObj, el) => {
                nextObj[`${el.index}`] = el?.value;
                return nextObj;
              }, {})
          );
        }
      });

      return tempFileHeaders;
    },

    convertHeadersArrayToRequest: (headers = [{ var: '', label: '', value: '' }]) => {
      const obj = {};
      headers.forEach((el, index) => {
        if (isPlainObject(el)) {
          set(obj, `${index}`, el.value);
        }
      });

      return obj;
    },
  };

  static mappingDataMapInListContacts(contacts) {
    if (!isArray(contacts)) {
      return [];
    }

    return contacts.map((el) => {
      const dataMap = isPlainObject(el?.dataMap) ? el.dataMap : {};

      return {
        ...dataMap,
        ...el,
      };
    });
  }

  static normalizeContactProcess(currentProcess, { totalContacts = 0 } = {}) {
    const processType = currentProcess?.type;

    const mappingFieldDataByProcessType = {
      target: {
        [StatusProcessContactGroup.isScrubbing]: 'taskData.data',
        [StatusProcessContactGroup.isUnarchving]: 'taskData.data',
        [StatusProcessContactGroup.isEnriching]: 'taskData.model',
        [StatusProcessContactGroup.isRefreshing]: 'taskData.model',
        [StatusProcessContactGroup.isCerting]: 'taskData',
        [StatusProcessContactGroup.isSplitting]: 'taskData',
        [StatusProcessContactGroup.isMerging]: 'taskData',
      },
      total: {
        [StatusProcessContactGroup.isScrubbing]: 'taskData.data.totalInGroup',
        [StatusProcessContactGroup.isUnarchving]: 'taskData.data.total',
        [StatusProcessContactGroup.isEnriching]: 'taskData.model.total',
        [StatusProcessContactGroup.isRefreshing]: 'taskData.model.total',
        [StatusProcessContactGroup.isCerting]: 'taskData.size',
        [StatusProcessContactGroup.isSplitting]: 'taskData.totalSize',
        [StatusProcessContactGroup.isMerging]: 'taskData.totalContact',
      },
      processed: {
        [StatusProcessContactGroup.isScrubbing]: 'taskData.data.processed',
        [StatusProcessContactGroup.isUnarchving]: 'taskData.data.saved',
        [StatusProcessContactGroup.isEnriching]: 'taskData.model.processed',
        [StatusProcessContactGroup.isRefreshing]: 'taskData.model.processed',
        [StatusProcessContactGroup.isCerting]: 'taskData.processed',
        [StatusProcessContactGroup.isSplitting]: 'taskData.totalMoved',
        [StatusProcessContactGroup.isMerging]: 'taskData.moved',
      },
    };

    const process = get(
      currentProcess,
      mappingFieldDataByProcessType?.target?.[processType] || 'taskData.data'
    );

    if (process) {
      const createdTime = currentProcess?.createdTime;
      const processTotal =
        get(currentProcess, mappingFieldDataByProcessType?.total?.[processType]) || totalContacts;
      const processedItem =
        get(currentProcess, mappingFieldDataByProcessType?.processed?.[processType]) || 0;

      return {
        ...process,
        totalContacts: totalContacts,
        timeRemaining: calculateRemainingTime({
          total: processTotal,
          processed: processedItem,
          created: createdTime,
          format: 'H[h] mm[m]',
        }),
        processTotal,
        processedItem,
      };
    }

    return undefined;
  }
}

export default ContactGroupModel;
